/**
 * Document Manager Component
 * Button that opens a modal showing attached documents with management options (delete, attach to chat)
 */

import React, { useState, useEffect } from 'react';
import { FaTimes, FaPlus, FaSync, FaInfoCircle, FaTrash, FaCheckCircle, FaClock, FaTimesCircle, FaExclamationTriangle } from 'react-icons/fa';
import { API_BASE_URL } from '../api/apiConfig';
import { linkDocumentsToConversation, createConversation } from '../api/conversationApi';
import { listEngDocs, deleteEngDoc } from '../api/engUploadApi';
import './DocumentManager.css';

const DocumentManager = ({ accessToken, onDocumentsChange, sessionId, conversationId, onDocumentsLinked, isOpen, onClose, selectedModel, mode = 'general', userId = '', showToast = (msg) => alert(msg) }) => {
  const isEng = mode === 'engineering';
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [activeTab, setActiveTab] = useState('my-files');

  // Sync internal modal state with external isOpen prop
  useEffect(() => {
    if (isOpen !== undefined) {
      setIsModalOpen(isOpen);
    }
  }, [isOpen]);

  // Handle modal close
  const handleClose = () => {
    setIsModalOpen(false);
    if (onClose) {
      onClose();
    }
  };

  // Fetch documents on mount and when sessionId changes
  useEffect(() => {
    if (accessToken) {
      fetchDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, sessionId]);

  const fetchDocuments = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let docs;
      if (isEng) {
        docs = await listEngDocs(userId);
      } else {
        const response = await fetch(`${API_BASE_URL}/api/documents`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) throw new Error('Failed to fetch documents');
        const data = await response.json();
        docs = data.documents || [];
      }

      setDocuments(docs);
      if (onDocumentsChange) onDocumentsChange(docs);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (docId, filename, linkedConversations = []) => {
    let confirmMessage = `Delete "${filename}"?\n\nThis will permanently remove the document from storage.`;
    if (!isEng && linkedConversations && linkedConversations.length > 0) {
      confirmMessage += `\n\nWARNING: This document is used in ${linkedConversations.length} conversation(s).`;
    }
    if (!window.confirm(confirmMessage)) return;

    try {
      let result;
      if (isEng) {
        result = await deleteEngDoc(docId, userId);
      } else {
        const response = await fetch(`${API_BASE_URL}/api/documents/${docId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${accessToken}` }
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to delete document');
        }
        result = await response.json();
      }
      
      console.log(`Document "${filename}" deleted`);
      await fetchDocuments();
      if (!isEng && result.linked_conversations && result.linked_conversations.length > 0) {
        showToast(`Document deleted. It was linked to ${result.linked_conversations.length} conversation(s).`, 'info');
      }
    } catch (err) {
      console.error('Error deleting document:', err);
      showToast(`Failed to delete document: ${err.message}`, 'error');
      // Refresh list anyway to sync with backend state
      await fetchDocuments();
    }
  };

  const handleAttachToChat = async () => {
    if (selectedDocuments.length === 0) {
      showToast('Please select at least one document to attach.', 'info');
      return;
    }

    try {
      setLoading(true);
      
      let targetConversationId = conversationId;
      
      // If no active conversation, create a new one
      if (!targetConversationId) {
        console.log('No active conversation - creating new one for documents');
        const newConv = await createConversation(
          accessToken,
          'New Chat with Documents',
          selectedModel || 'llama-4-maverick' // Use selected model or default to llama
        );
        targetConversationId = newConv.id;
        console.log(`Created new conversation: ${targetConversationId}`);
        
        // Notify parent to switch to this new conversation
        if (onDocumentsLinked) {
          onDocumentsLinked({ conversationId: targetConversationId, documents: [] });
        }
      }
      
      const result = await linkDocumentsToConversation(
        accessToken,
        targetConversationId,
        selectedDocuments
      );

      if (result.success) {
        showToast(`Successfully attached ${result.linked.length} document(s) to conversation!`, 'success');
        
        // Get full document details for the linked documents
        const linkedDocDetails = documents.filter(doc => 
          result.linked.some(linkedId => linkedId === doc.id)
        );
        
        // Clear selection
        setSelectedDocuments([]);
        
        // Notify parent component with full document details
        if (onDocumentsLinked) {
          onDocumentsLinked({
            conversationId: targetConversationId,
            documents: linkedDocDetails
          });
        }
        
        // Refresh documents to update linked conversations count
        await fetchDocuments();
        
        // Close modal after successful attach
        handleClose();
      } else {
        const failedCount = result.failed.length;
        if (failedCount > 0) {
          showToast(`Failed to attach ${failedCount} document(s). Check console for details.`, 'error');
          console.error('Failed documents:', result.failed);
        }
      }
    } catch (err) {
      console.error('Error attaching documents:', err);
      showToast(`Failed to attach documents: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleDocumentSelection = (docId) => {
    setSelectedDocuments(prev => {
      if (prev.includes(docId)) {
        return prev.filter(id => id !== docId);
      } else {
        return [...prev, docId];
      }
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return 'Unknown';
    }
  };
  
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getStatusBadge = (status) => {
    const badges = {
      'ready':      { icon: <FaCheckCircle />,       label: '',      class: 'status-ready' },
      'processing': { icon: <FaClock />,              label: '', class: 'status-processing' },
      'failed':     { icon: <FaTimesCircle />,        label: '',     class: 'status-failed' }
    };
    
    const badge = badges[status] || badges['ready'];
    return (
      <span className={`status-badge ${badge.class}`}>
        {badge.icon} {badge.label}
      </span>
    );
  };

  return (
    <>
      {/* Modal */}
      {isModalOpen && (
        <div className="document-modal-overlay" onClick={handleClose}>
          <div className="document-modal" onClick={(e) => e.stopPropagation()}>
            <div className="document-modal-header">
              <div className="document-modal-header-text">
                <h3>{isEng ? 'Engineering Documents' : 'Files'}</h3>
              </div>
              <div className="modal-actions">
                {/* Attach-to-chat only available in general mode, My Files tab */}
                {!isEng && activeTab === 'my-files' && documents.length > 0 && (
                  <button 
                    className="attach-button" 
                    onClick={handleAttachToChat}
                    disabled={loading || selectedDocuments.length === 0}
                    title={conversationId ? 'Attach selected documents to current chat' : 'Create new chat with selected documents'}
                  >
                    <FaPlus/>
                  </button>
                )}
                {activeTab === 'my-files' && (
                  <button 
                    className="refresh-btn" 
                    onClick={fetchDocuments} 
                    disabled={loading} 
                    title="Refresh list"
                  >
                    <FaSync />
                  </button>
                )}
                <button
                  className="close-modal-button"
                  onClick={handleClose}
                  title="Close"
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            {/* Tabs — only shown in general mode */}
            {!isEng && (
              <div className="document-modal-tabs">
                <button
                  className={`doc-tab${activeTab === 'my-files' ? ' doc-tab--active' : ''}`}
                  onClick={() => setActiveTab('my-files')}
                >
                  My Files
                  {activeTab === 'my-files' && (
                    <span className="doc-tab-hint-wrapper">
                      <FaInfoCircle className="doc-info-icon" />
                      <span className="doc-tab-hint">Files expire 30 days after upload</span>
                    </span>
                  )}
                </button>
                <button
                  className={`doc-tab${activeTab === 'project-files' ? ' doc-tab--active' : ''}`}
                  onClick={() => setActiveTab('project-files')}
                >
                  Project Files
                </button>
              </div>
            )}

            <div className="document-modal-content">
              {activeTab === 'project-files' ? (
                <div className="no-documents">
                  <span className="no-documents-icon">🏗️</span>
                  <p>Project Files coming soon</p>
                  <p className="hint">This tab will display files from your SharePoint site.</p>
                </div>
              ) : loading && documents.length === 0 ? (
                <div className="no-documents">
                  <div className="loading-spinner" />
                  <p>Loading documents...</p>
                </div>
              ) : error ? (
                <div className="error-container">
                  <p className="error-text"><FaExclamationTriangle /> {error}</p>
                  <button className="retry-button" onClick={fetchDocuments}>
                    Retry
                  </button>
                </div>
              ) : documents.length === 0 ? (
                <div className="no-documents">
                  <span className="no-documents-icon">📄</span>
                  <p>No documents uploaded yet</p>
                  <p className="hint">{isEng ? 'Upload engineering documents (PDF, DOCX, PNG, JPG) using the 📎 button in the chat.' : 'Drag & drop files into chat, paste from clipboard, or use the upload button.'}</p>
                </div>
              ) : (
                <div className="documents-list">
                  {documents.map((doc) => {
                    return (
                      <div 
                        key={doc.id} 
                        className={`document-item ${!isEng && selectedDocuments.includes(doc.id) ? 'selected' : ''}`}
                        onClick={() => !isEng && toggleDocumentSelection(doc.id)}
                        style={{ cursor: isEng ? 'default' : 'pointer' }}
                      >
                        {!isEng && (
                          <input
                            type="checkbox"
                            checked={selectedDocuments.includes(doc.id)}
                            onChange={() => toggleDocumentSelection(doc.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="document-checkbox"
                          />
                        )}
                        <div
                          className="document-info"
                          title={
                            isEng
                              ? `${doc.filename}\nType: ${doc.content_kind?.toUpperCase() || 'DOC'}\nUploaded: ${formatDate(doc.uploaded_at)}`
                              : `${doc.filename}\nSize: ${formatFileSize(doc.file_size_bytes)}\nStatus: ${doc.status || 'ready'}\nUploaded: ${formatDate(doc.uploaded_at)}${doc.expires_at ? `\nExpires: ${formatDate(doc.expires_at)}` : ''}`
                          }
                        >
                          <div className="document-name">
                            {doc.filename}
                          </div>
                          <div className="document-meta">
                            {isEng ? (
                              <span className="status-badge status-ready">
                                {doc.content_kind?.toUpperCase() || 'DOC'}
                              </span>
                            ) : (
                              getStatusBadge(doc.status)
                            )}
                          </div>
                        </div>
                        <button
                          className="delete-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(doc.id, doc.filename, doc.linked_conversations);
                          }}
                          title="Delete document"
                          disabled={loading}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer summary */}
            {documents.length > 0 && activeTab === 'my-files' && (
              <div className="document-modal-footer">
                {isEng ? (
                  <span className="documents-summary-text">Documents are auto-included when you chat</span>
                ) : (
                  <span className="documents-summary-text">
                    {selectedDocuments.length > 0
                      ? `${selectedDocuments.length} of ${documents.length} selected — attach to include in conversation`
                      : 'Select files to attach to your current chat'}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default DocumentManager;
